import { EventEmitter } from '@angular/core';
import { Observable, Subject, Subscription, combineLatest, merge } from 'rxjs';
import { map, distinctUntilChanged, delay, switchMap } from 'rxjs/operators';
import {
  AgGridEvent,
  CellClassParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridSizeChangedEvent
} from 'ag-grid-community';
import {
  InfiniteScrollingService,
  stringComparer,
  numberComparer,
  ReusableStreamProperty,
  equalsCompare
} from '@ipreo/muni-shared';
import { maturityComparator } from '../../mappers/monitor.calculations';
import { MonitorBasePageService } from '../../services/monitor-base-page.service';
import { NegativeCasesService } from '../../../gameday/services/negative-cases.service';
import { GameDayCalculatedData } from '../../../gameday/data/gameDay-data.calculation';
import { ColumnDefinition } from '../../../models/column';

export function closedMaturityDecider({ data }: CellClassParams) {
  return data
    ? MonitorBasePageComponent.closedMaturityIds.includes(data.id)
    : false;
}

export function colSpanCalculator(params) {
  return params.node?.rowPinned === 'top' ? 3 : 1;
}

export const monitorBasePageSassVariables = {
  colorRedDarker: '#a11010'
};

export abstract class MonitorBasePageComponent<
  TService extends MonitorBasePageService
> {
  static closedMaturityIds: string[] = [];
  static readonly columnDefinition: ColumnDefinition[] = [
    {
      field: 'maturity',
      headerName: 'Maturity',
      label: 'Maturity',
      sort: 'asc',
      comparator: maturityComparator,
      position: 1,
      width: 110,
      minWidth: 110,
      maxWidth: 110,
      cellClassRules: {
        'closed-maturity': closedMaturityDecider
      },
      colSpan: colSpanCalculator,
      suppressHeaderMenuButton: true
    },
    {
      field: 'series',
      headerName: 'Series',
      label: 'Series',
      comparator: stringComparer,
      position: 2,
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      suppressHeaderMenuButton: true
    },
    {
      field: 'coupon',
      headerName: 'Coupon',
      label: 'Coupon',
      type: 'numericColumn',
      comparator: numberComparer,
      position: 3,
      width: 95,
      minWidth: 95,
      maxWidth: 95,
      suppressHeaderMenuButton: true
    },
    {
      field: 'amount',
      headerName: 'Amount',
      label: 'Amount',
      type: 'numericColumn',
      comparator: numberComparer,
      position: 4,
      width: 110,
      minWidth: 110,
      maxWidth: 110,
      suppressHeaderMenuButton: true
    }
  ];

  private readonly gridReadySubj = new Subject<GridReadyEvent>();
  private readonly gridColumnDefSubj = new Subject<
    Observable<ColumnDefinition[]>
  >();
  private readonly gridScrollSubj = new Subject<string>();
  private readonly gridResizeSubj = new Subject<AgGridEvent>();

  @ReusableStreamProperty
  private readonly grid$: Observable<[ColumnDefinition[], GridReadyEvent]> =
    combineLatest([
      this.gridColumnDefSubj.pipe(
        switchMap((colDefs: Observable<ColumnDefinition[]>) => colDefs)
      ),
      this.gridReadySubj
    ]).pipe(
      distinctUntilChanged((prev, curr) => {
        return (
          prev &&
          curr &&
          equalsCompare(prev[0], curr[0]) &&
          prev[1] === curr[1]
        );
      })
    );

  @ReusableStreamProperty
  private readonly selectedMaturityId$: Observable<string> =
    this.monitorPageService.selectedMaturityId$;
  private selectedMaturityIdSubscription: Subscription =
    this.selectedMaturityId$.subscribe(
      (id: string) => (this.selectedMaturityId = id)
    );
  private closedMaturityIdsSubscription: Subscription =
    this.monitorPageService.maturityIdsWithOrderPeriodTerminated.subscribe(
      (ids: string[]) => (MonitorBasePageComponent.closedMaturityIds = ids)
    );
  private gridSubscription: Subscription = this.grid$.subscribe(
    ([columnDefinitions, event]: [ColumnDefinition[], GridReadyEvent]) => {
      const { api } = event;
      api.setGridOption('columnDefs', columnDefinitions);
      this.gridScrollSubj.next(this.selectedMaturityId);
      this.gridResizeSubj.next(event);
    }
  );

  private scrollSubscription: Subscription = combineLatest([
    merge(this.gridScrollSubj, this.selectedMaturityId$),
    this.gridReadySubj
  ]).subscribe(([id, { api }]: [string, GridReadyEvent]) =>
    this.scrollIntoView(id, api)
  );

  private resizeSubscription: Subscription = this.gridResizeSubj
    .pipe(delay(0))
    .subscribe(({ api }: GridSizeChangedEvent) => {
      api.sizeColumnsToFit();
    });

  public gridOptions: GridOptions = this.monitorPageService.defaultGridOptions;
  public selectedMaturityId: string;
  public emptyMessage: Observable<string> =
    this.negativeCasesService.messages.pipe(
      map((messages) => messages.chart),
      distinctUntilChanged()
    );

  public set dataSource(value: GameDayCalculatedData) {
    this.monitorPageService.dataSourceSubj = value;
  }

  maturityClick: EventEmitter<{ id: string }> = new EventEmitter<{ id }>();

  constructor(
    protected monitorPageService: TService,
    private negativeCasesService: NegativeCasesService,
    protected infiniteScrollingService: InfiniteScrollingService
  ) {}

  private scrollIntoView(id: string, api: GridApi) {
    if (id) {
      const selectedNodes = api.getSelectedNodes();
      if (selectedNodes.length) {
        api.ensureIndexVisible(selectedNodes[0].rowIndex);
      }
    } else {
      api.deselectAll();
    }
  }

  public onGridReady(
    event: GridReadyEvent,
    columnDefs: Observable<ColumnDefinition[]>
  ) {
    this.gridReadySubj.next(event);
    this.gridColumnDefSubj.next(columnDefs);
  }

  public onGridSizeChanged(event: GridSizeChangedEvent) {
    this.gridResizeSubj.next(event);
    this.gridScrollSubj.next(this.selectedMaturityId);
  }

  public onSelectMaturity({ data: { id } }) {
    this.maturityClick.emit({ id });
  }

  public onDestroy(): void {
    this.selectedMaturityIdSubscription.unsubscribe();
    this.closedMaturityIdsSubscription.unsubscribe();
    this.gridSubscription.unsubscribe();
    this.scrollSubscription.unsubscribe();
    this.resizeSubscription.unsubscribe();
  }
}
